import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSpinnerOverlayComponent } from 'src/app/components/mat-spinner-overlay/mat-spinner-overlay.component';
import * as XLSX from 'xlsx';
import { SnackBarService } from '../../services/snack-bar-service.service';
import { AdmRedePosto, PostosMaster } from '../models';
import { AdmRedePostosService } from '../services/adm-rede-postos.service';
import { StringNormalizer } from '../utils';

/**
 * Component for displaying and managing posts table
 */
@Component({
  selector: 'app-tabela-postos',
  templateUrl: './tabela-postos.component.html',
  styleUrls: ['./tabela-postos.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatSelectModule,
    MatExpansionModule,
    MatSpinnerOverlayComponent,
  ],
})
export class TabelaPostosComponent implements OnInit, AfterViewInit {
  // ==================== Dependencies ====================
  private readonly service = inject(AdmRedePostosService);
  private readonly snack = inject(SnackBarService);

  // ==================== Inputs/Outputs ====================
  @Input() admRedePosto?: AdmRedePosto;
  @Output() totalPostosAtreladosChange = new EventEmitter<number>();

  // ==================== ViewChild ====================
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  // ==================== State ====================
  public loading: boolean = false;
  private _postos: PostosMaster[] = [];

  // ==================== Getters ====================
  get postos(): PostosMaster[] {
    return this._postos;
  }

  get totalPostosAtrelados(): number {
    return this._postos.filter((p) => p.postoAtrelado).length;
  }

  get hasUnsavedChanges(): boolean {
    return this._postos.some((p) => p.hasChanges());
  }

  get unsavedChangesCount(): number {
    return this._postos.filter((p) => p.hasChanges()).length;
  }

  // ==================== Data Source ====================
  readonly dataSource = new MatTableDataSource<PostosMaster>([]);
  readonly selection = new SelectionModel<PostosMaster>(true, []);

  // ==================== Display Columns ====================
  readonly displayedColumns: string[] = [
    'select',
    'codPosto',
    'nomePosto',
    'nomeFantasia',
    'descricaoRede',
    'cnpj',
  ];

  // ==================== Forms ====================
  readonly filterForm = new FormGroup({
    codPosto: new FormControl(''),
    nomePosto: new FormControl(''),
    nomeFantasia: new FormControl(''),
    descricaoRede: new FormControl(''),
    cnpj: new FormControl(''),
    vinculado: new FormControl(''), // '', 'sim', 'nao'
  });

  // ==================== Lifecycle ====================
  ngOnInit(): void {
    if (this.admRedePosto) {
      this.loadPostos();
    }
  }

  ngAfterViewInit(): void {
    this.setupDataSource();
  }

  // ==================== Setup Methods ====================
  private setupDataSource(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.configureSorting();
    this.configureFiltering();
  }

  private configureSorting(): void {
    this.dataSource.sortingDataAccessor = (
      data: PostosMaster,
      sortHeaderId: string
    ) => {
      switch (sortHeaderId) {
        case 'codPosto':
          return data.codPosto;
        case 'nomePosto':
          return StringNormalizer.normalizeForSorting(data.nomePosto);
        case 'nomeFantasia':
          return StringNormalizer.normalizeForSorting(data.nomeFantasia);
        case 'descricaoRede':
          return StringNormalizer.normalizeForSorting(data.descricaoRede);
        case 'cnpj':
          return StringNormalizer.normalizeForSorting(data.cnpj);
        default:
          return (data as any)[sortHeaderId] || '';
      }
    };
  }

  private configureFiltering(): void {
    this.dataSource.filterPredicate = (
      data: PostosMaster,
      filter: string
    ): boolean => {
      const filters = JSON.parse(filter);
      const normalize = StringNormalizer.normalizeForSearch;

      // Parse codPosto filter (comma-separated numbers)
      const codPostoStr = filters.codPosto ? String(filters.codPosto) : '';
      const codigosPostos: number[] = codPostoStr
        .split(',')
        .map((cod: string) => parseInt(cod.trim(), 10))
        .filter((cod: number) => !isNaN(cod));

      const matchCodPosto =
        !filters.codPosto ||
        (data.codPosto !== undefined && codigosPostos.includes(data.codPosto));

      const matchNomePosto =
        !filters.nomePosto ||
        normalize(data.nomePosto).includes(normalize(filters.nomePosto));

      const matchNomeFantasia =
        !filters.nomeFantasia ||
        normalize(data.nomeFantasia).includes(normalize(filters.nomeFantasia));

      const matchDescricaoRede =
        !filters.descricaoRede ||
        normalize(data.descricaoRede).includes(
          normalize(filters.descricaoRede)
        );

      const matchCnpj =
        !filters.cnpj || normalize(data.cnpj).includes(normalize(filters.cnpj));

      const matchVinculado =
        !filters.vinculado ||
        (filters.vinculado === 'sim' && data.postoAtrelado) ||
        (filters.vinculado === 'nao' && !data.postoAtrelado);

      return (
        matchCodPosto &&
        matchNomePosto &&
        matchNomeFantasia &&
        matchDescricaoRede &&
        matchCnpj &&
        matchVinculado
      );
    };
  }

  // ==================== Data Loading ====================
  private loadPostos(): void {
    if (!this.admRedePosto) return;

    this.loading = true;
    this.service.buscarListaPostosAsync(this.admRedePosto).subscribe({
      next: (postos) => {
        // Initialize state tracking
        postos.forEach((posto) => {
          posto.PostoAtreladoAtual = posto.postoAtrelado;
          if (posto.postoAtrelado) {
            this.selection.select(posto);
          }
        });

        this._postos = postos;
        this.dataSource.data = postos;
        this.reconfigureSort();
        this.emitTotalPostos();
      },
      error: () => {
        this.snack.flashMessage(
          'Erro ao carregar os postos vinculados à macro',
          ['snackbar-error'],
          'Fechar',
          3000
        );
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  private reconfigureSort(): void {
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  private emitTotalPostos(): void {
    this.totalPostosAtreladosChange.emit(this.totalPostosAtrelados);
  }

  // ==================== Filter Methods ====================
  applyFilter(): void {
    const filterValues = this.filterForm.value;
    this.dataSource.filter = JSON.stringify(filterValues);
    this.dataSource.paginator?.firstPage();
  }

  clearFilter(): void {
    this.filterForm.reset();
    this.dataSource.filter = '';
    this.dataSource.paginator?.firstPage();
  }

  onCodPostoInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Remove non-numeric characters except commas
    value = value.replace(/[^0-9,]/g, '');

    // Remove consecutive commas
    value = value.replace(/,,+/g, ',');

    input.value = value;
    this.filterForm.get('codPosto')?.setValue(value);
  }

  // ==================== Selection Methods ====================
  getItensDaPaginaAtual(): PostosMaster[] {
    if (!this.paginator) return [];

    const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
    const endIndex = startIndex + this.paginator.pageSize;
    return this.dataSource.filteredData.slice(startIndex, endIndex);
  }

  masterToggle(): void {
    const itensDaPagina = this.getItensDaPaginaAtual();

    if (this.isAllSelected()) {
      this.selection.clear();
      itensDaPagina.forEach((posto) => (posto.postoAtrelado = false));
    } else {
      itensDaPagina.forEach((posto) => {
        this.selection.select(posto);
        posto.postoAtrelado = true;
      });
    }
  }

  isAllSelected(): boolean {
    const itensDaPagina = this.getItensDaPaginaAtual();
    if (itensDaPagina.length === 0) return false;

    return itensDaPagina.every(
      (posto) => this.selection.isSelected(posto) && posto.postoAtrelado
    );
  }

  alterouCheckBox(event: MatCheckboxChange, posto: PostosMaster): void {
    posto.postoAtrelado = event.checked;

    if (event.checked) {
      this.selection.select(posto);
    } else {
      this.selection.deselect(posto);
    }
  }

  // ==================== Save Methods ====================
  salvarAlteracoes(): void {
    const postosAlterados = this._postos.filter((p) => p.hasChanges());

    if (postosAlterados.length === 0) {
      this.snack.flashMessage(
        'Nenhuma alteração foi detectada nos postos',
        ['snackbar-info'],
        'Fechar',
        3000
      );
      return;
    }

    if (!this.admRedePosto) return;

    this.loading = true;

    const payload = {
      idAdmRedePosto: this.admRedePosto.idAdmRedePosto,
      master: this.admRedePosto.nome,
      postos: postosAlterados.map((posto) => ({
        codPosto: posto.codPosto,
        postoAtrelado: posto.postoAtrelado,
      })),
    };

    this.service.salvarAlteracoesPosto(payload).subscribe({
      next: (result) => {
        // Update initial state after successful save
        postosAlterados.forEach((posto) => {
          posto.PostoAtreladoAtual = posto.postoAtrelado;
        });
        //ordenar postos novamente por atrelados
        this.dataSource.data = this.dataSource.data.sort((a, b) =>
          a.postoAtrelado ? -1 : 1
        );

        this.emitTotalPostos();

        this.snack.flashMessage(
          `Postos na Macro: ${payload.master} atualizados com sucesso!`,
          ['snackbar-success'],
          'Fechar',
          3000
        );
      },
      error: () => {
        this.snack.flashMessage(
          'Erro ao atualizar os postos da macro',
          ['snackbar-error'],
          'Fechar',
          3000
        );
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  // ==================== Export Methods ====================
  exportToExcel(): void {
    const data = this.prepareExcelData();
    const workSheet = XLSX.utils.json_to_sheet(data);
    const workBook = XLSX.utils.book_new();
    const fileName = this.service.gerarNomeArquivo('Postos_vinculados_');
    XLSX.utils.book_append_sheet(workBook, workSheet, fileName);
    XLSX.writeFile(workBook, `${fileName}.xlsx`);
  }

  private prepareExcelData(): Array<{
    'Vinculado a Macro?': string;
    'Código do Posto': number;
    Nome: string;
    Fantasia: string;
    'Descrição da Rede': string;
    CNPJ: string;
  }> {
    return this.dataSource.filteredData.map((item) => ({
      'Vinculado a Macro?': item.postoAtrelado ? 'Sim' : 'Não',
      'Código do Posto': item.codPosto,
      Nome: item.nomePosto,
      Fantasia: item.nomeFantasia,
      'Descrição da Rede': item.descricaoRede,
      CNPJ: item.cnpj,
    }));
  }
}
